import { useEffect, useRef, type RefObject } from "react";
import { czyFokusZginal, najblizszyPrzycisk } from "../../lib/fokus.js";
import { pozycjaWpisu, type WpisUwagi } from "../../lib/uwagiWypowiedzi.js";
import { FormularzUwagi } from "./FormularzUwagi.js";
import { UwagaZapisana } from "./UwagaZapisana.js";

/**
 * Uwagi recenzenta pod jedna wypowiedzia (albo w karcie uwag bez wypowiedzi): zapisane uwagi jako karty, otwarte
 * formularze w miejscu swojej uwagi. Wpisy maja stabilne klucze, wiec pierwszy zapis nowej uwagi nie odmontowuje
 * formularza (nie traci tekstu ani fokusu).
 *
 * Fokus (A-4): formularz zamkniety przez "Gotowe" i uwaga usunieta zabieraja przycisk, ktory go mial, a przegladarka
 * zrzuca wtedy fokus na <body>. Po takiej zmianie przenosimy go na "Edytuj" uwagi, ktora wlasnie sie zapisala, a gdy
 * uwagi juz nie ma, na przycisk sasiedniej uwagi albo na "Dodaj uwage" przy wypowiedzi (`przyciskDodaj`). Gdy
 * uzytkownik zdazyl pojsc gdzie indziej, niczego mu nie zabieramy.
 */
export function ListaUwag({
  wpisy,
  id,
  przyciskDodaj,
}: {
  wpisy: readonly WpisUwagi[];
  id?: string;
  przyciskDodaj?: RefObject<HTMLButtonElement | null>;
}): React.JSX.Element | null {
  const refKorzenia = useRef<HTMLDivElement>(null);
  const poprzednie = useRef(wpisy);

  useEffect(() => {
    const stare = poprzednie.current;
    poprzednie.current = wpisy;
    if (stare === wpisy || !czyFokusZginal()) return;

    const teraz = new Map(wpisy.map((w) => [w.klucz, w] as const));
    // Pierwszy wpis, ktory przestal byc formularzem albo zniknal z listy.
    const indeks = stare.findIndex((s) => {
      const t = teraz.get(s.klucz);
      return t === undefined || (s.otwarta && !t.otwarta);
    });
    if (indeks < 0) return;

    const korzen = refKorzenia.current;
    const zapisana = teraz.get(stare[indeks].klucz);
    if (zapisana?.uwaga) {
      const karta = Array.from(korzen?.querySelectorAll<HTMLElement>("[data-klucz]") ?? []).find(
        (el) => el.getAttribute("data-klucz") === zapisana.klucz,
      );
      karta?.querySelector<HTMLElement>(".rec-uwaga__edytuj")?.focus();
      return;
    }
    // Uwagi nie ma: najblizszy przycisk wsrod kart, ktore zostaly, a w ostatecznosci "Dodaj uwage".
    const karty = Array.from(korzen?.querySelectorAll<HTMLElement>(".rec-uwaga:not(.rec-uwaga--formularz)") ?? []);
    const pozycja = stare.slice(0, indeks).filter((s) => !s.otwarta).length;
    (najblizszyPrzycisk(karty, pozycja) ?? przyciskDodaj?.current)?.focus();
  }, [wpisy, przyciskDodaj]);

  if (wpisy.length === 0) return null;

  return (
    <div className="rec-uwagi" id={id} ref={refKorzenia}>
      {wpisy.map((wpis) => {
        // Kilka uwag pod jedna wypowiedzia dostaje w nazwie grupy miejsce ("uwaga 2 z 3"), zeby czytnik je odroznil.
        const pozycja = pozycjaWpisu(wpisy, wpis);
        return wpis.otwarta ? (
          <FormularzUwagi key={wpis.klucz} wpis={wpis} pozycja={pozycja} />
        ) : (
          <UwagaZapisana key={wpis.klucz} wpis={wpis} pozycja={pozycja} />
        );
      })}
    </div>
  );
}
